import React, { useState, useEffect } from 'react';
import {
  Activity, AlertTriangle, Archive, Ban, Bot, Building2, Check, Cloud, GitCommit, Globe, Info,
  Loader2, Mail, Package, Pencil, Plane, Plus, RefreshCw, Settings, Settings2, ShieldCheck,
  Star, Trash2, UserCog, Users, X
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import CreateOrganizationModal from './CreateOrganizationModal';
import InviteUserModal from './InviteUserModal';
import InviteLinkModal from './InviteLinkModal';
import UserDeleteModal from './UserDeleteModal';
import ManageOrgAdminsModal from './ManageOrgAdminsModal';
import OrganizationComponentSettingsModal from './OrganizationComponentSettingsModal';
import OrganizationDeleteModal from './OrganizationDeleteModal';
import SSODomainModal from './SSODomainModal';
import FlightAwareLogs from './FlightAwareLogs';
import AIUsageLogs from './AIUsageLogs';
import RejectedEmailLogs from './RejectedEmailLogs';
import PackageFormModal from './PackageFormModal';
import AssignPackageModal from './AssignPackageModal';
import ParsingFeedbackAdmin from './ParsingFeedbackAdmin';
import { listEmailDomains, addEmailDomain, updateEmailDomain, setPrimaryDomain, deleteEmailDomain } from '../../services/emailDomainService';

const SuperadminDashboard = () => {
  const [activeTab, setActiveTab] = useState('organizations');
  const [organizations, setOrganizations] = useState([]);
  const [deletedOrganizations, setDeletedOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [packages, setPackages] = useState([]);
  const [systemSettings, setSystemSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showInviteUserModal, setShowInviteUserModal] = useState(false);
  const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
  const [showUserDeleteModal, setShowUserDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showOrgDeleteModal, setShowOrgDeleteModal] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [showManageAdminsModal, setShowManageAdminsModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [showComponentSettingsModal, setShowComponentSettingsModal] = useState(false);
  const [selectedOrgForSettings, setSelectedOrgForSettings] = useState(null);
  const [showSSODomainModal, setShowSSODomainModal] = useState(false);
  const [selectedOrgForSSO, setSelectedOrgForSSO] = useState(null);
  // Package management state
  const [showPackageFormModal, setShowPackageFormModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showAssignPackageModal, setShowAssignPackageModal] = useState(false);
  // User management state
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);
  // Email domains state
  const [emailDomains, setEmailDomains] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role === 'superadmin') {
      loadData();
    }
  }, [user, activeTab, showInactiveUsers]);

  const loadData = async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const requests = [
        axios.get(`${apiBase}/admin/organizations`, { headers }),
        axios.get(`${apiBase}/admin/users${showInactiveUsers ? '?include_inactive=true' : ''}`, { headers }),
        axios.get(`${apiBase}/admin/invites`, { headers }),
        axios.get(`${apiBase}/admin/packages`, { headers }),
        axios.get(`${apiBase}/admin/system-settings`, { headers })
      ];

      // Load deleted organizations if on that tab
      if (activeTab === 'deleted') {
        requests.push(axios.get(`${apiBase}/admin/organizations/deleted`, { headers }));
      }

      const responses = await Promise.all(requests);
      const [orgsResponse, usersResponse, invitesResponse, packagesResponse, settingsResponse, deletedOrgsResponse] = responses;

      setOrganizations(orgsResponse.data);
      setUsers(usersResponse.data);
      setInvites(invitesResponse.data);
      setPackages(packagesResponse.data);
      setSystemSettings(settingsResponse.data);

      if (deletedOrgsResponse) {
        setDeletedOrganizations(deletedOrgsResponse.data);
      }

      // Load email domains if on domains tab
      if (activeTab === 'domains') {
        try {
          const domains = await listEmailDomains(true);
          setEmailDomains(domains);
        } catch (domainErr) {
          console.error('Error loading email domains:', domainErr);
        }
      }
    } catch (err) {
      console.error('Error loading superadmin data:', err);
      setError('Failed to load data: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async (orgData) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.post(`${apiBase}/admin/organizations`, orgData, { headers });
      setShowCreateOrgModal(false);
      await loadData(); // Reload data
    } catch (err) {
      console.error('Failed to create organization in dashboard:', err);
      setError('Failed to create organization: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleInviteUser = async (inviteData) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.post(`${apiBase}/admin/invite-user`, inviteData, { headers });
      setShowInviteUserModal(false);
      
      // Show success with invite link modal
      if (response.data.status === 'approved') {
        setError(''); // Clear any previous errors
        
        // Find organization name
        const organization = organizations.find(org => org.id === inviteData.organization_id);
        const organizationName = organization ? organization.name : 'the organization';
        
        // Set up invite result for the modal
        setInviteResult({
          inviteLink: response.data.invite_link,
          inviteToken: response.data.invite_token,
          email: inviteData.email,
          organizationName: organizationName,
          role: inviteData.role,
          emailSent: response.data.email_sent || false
        });

        setShowInviteLinkModal(true);
      }

      loadData();
    } catch (err) {
      console.error('Failed to invite user:', err);
      setError('Failed to invite user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleApproveInvite = async (inviteId) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.post(`${apiBase}/admin/approve-invite/${inviteId}`, {}, { headers });
      loadData();
    } catch (err) {
      setError('Failed to approve invite: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRejectInvite = async (inviteId) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.post(`${apiBase}/admin/reject-invite/${inviteId}`, {}, { headers });
      loadData();
    } catch (err) {
      setError('Failed to reject invite: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleManageOrgAdmins = (organization) => {
    setSelectedOrganization(organization);
    setShowManageAdminsModal(true);
  };

  const handleComponentSettings = (org) => {
    setSelectedOrgForSettings(org);
    setShowComponentSettingsModal(true);
  };

  const handleSSODomains = (org) => {
    setSelectedOrgForSSO(org);
    setShowSSODomainModal(true);
  };

  // Package Management Handlers
  const handleSavePackage = async (packageData) => {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();

    if (selectedPackage) {
      // Update existing package
      await axios.put(`${apiBase}/admin/packages/${selectedPackage.id}`, packageData, { headers });
    } else {
      // Create new package
      await axios.post(`${apiBase}/admin/packages`, packageData, { headers });
    }

    await loadData();
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm('Are you sure you want to archive this package? It will no longer be available for new assignments.')) {
      return;
    }

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.delete(`${apiBase}/admin/packages/${packageId}`, { headers });
      await loadData();
    } catch (err) {
      setError('Failed to archive package: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdateOrgAdmins = async () => {
    setShowManageAdminsModal(false);
    setSelectedOrganization(null);
    await loadData();
  };

  const handleDeleteOrganization = async (orgId) => {
    // Find the organization details for the modal
    const org = organizations.find(o => o.id === orgId);
    if (!org) {
      setError('Organization not found');
      return;
    }
    
    // Set up for deletion and show modal
    setOrgToDelete(org);
    setShowOrgDeleteModal(true);
  };

  const confirmOrganizationDelete = async () => {
    if (!orgToDelete) return;
    
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.delete(`${apiBase}/admin/organizations/${orgToDelete.id}`, { headers });
      alert(`Organization "${orgToDelete.name}" has been permanently deleted.`);
      await loadData();
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Failed to delete organization:', err);
      setError('Failed to delete organization: ' + (err.response?.data?.detail || err.message));
    } finally {
      setShowOrgDeleteModal(false);
      setOrgToDelete(null);
    }
  };

  const cancelOrganizationDelete = () => {
    setShowOrgDeleteModal(false);
    setOrgToDelete(null);
  };

  const handleReinstateOrganization = async (orgId) => {
    if (window.confirm('Are you sure you want to reinstate this organization? This will restore all users and data.')) {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        await axios.post(`${apiBase}/admin/organizations/${orgId}/reinstate`, {}, { headers });
        alert('Organization reinstated successfully!');
        await loadData();
      } catch (err) {
        setError('Failed to reinstate organization: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handlePermanentlyDelete = async (orgId) => {
    if (window.confirm('Are you sure you want to PERMANENTLY delete this organization? This action cannot be undone and will remove all data forever.')) {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        await axios.delete(`${apiBase}/admin/organizations/${orgId}/permanent`, { headers });
        alert('Organization permanently deleted.');
        await loadData();
      } catch (err) {
        setError('Failed to permanently delete organization: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleRemoveUser = async (userId) => {
    // Find the user details for the confirmation modal
    const user = users.find(u => u.id === userId);
    if (!user) {
      console.error('User not found for deletion:', userId);
      setError('User not found - cannot remove');
      return;
    }

    // Set up for deletion and show custom confirmation modal
    setUserToDelete(user);
    setShowUserDeleteModal(true);
  };

  const confirmUserDelete = async () => {
    if (!userToDelete) return;

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.delete(`${apiBase}/admin/users/${userToDelete.id}`, { headers });

      // Remove the deleted user from local state
      const updatedUsers = users.filter(user => user.id !== userToDelete.id);

      setUsers(updatedUsers);
      setError('');

      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-ui-green-bg border border-ui-green-line text-ui-green-fg px-4 py-3 rounded-lg shadow-ui-pop z-50';
      successMessage.textContent = `User ${userToDelete.first_name} ${userToDelete.last_name} deactivated successfully`;
      document.body.appendChild(successMessage);
      
      // Remove success message after 3 seconds
      setTimeout(() => {
        if (document.body.contains(successMessage)) {
          document.body.removeChild(successMessage);
        }
      }, 3000);
      
    } catch (error) {
      console.error('Delete user error:', error);
      let errorMessage = 'Failed to remove user';
      
      if (error.response?.status === 403) {
        errorMessage = 'Permission denied. You cannot remove this user.';
      } else if (error.response?.status === 404) {
        errorMessage = 'User not found or already removed.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setError(errorMessage);
    } finally {
      setShowUserDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const cancelUserDelete = () => {
    setShowUserDeleteModal(false);
    setUserToDelete(null);
  };

  const handleReactivateUser = async (userId) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.post(`${apiBase}/admin/users/${userId}/reactivate`, {}, { headers });

      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-ui-green-bg border border-ui-green-line text-ui-green-fg px-4 py-3 rounded-lg shadow-ui-pop z-50';
      successMessage.textContent = 'User reactivated successfully';
      document.body.appendChild(successMessage);
      setTimeout(() => {
        if (document.body.contains(successMessage)) {
          document.body.removeChild(successMessage);
        }
      }, 3000);

      loadData(); // Reload to get updated user list
    } catch (err) {
      setError('Failed to reactivate user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handlePermanentlyDeleteUser = async (userId) => {
    const userToDelete = users.find(u => u.id === userId);
    if (!userToDelete) return;

    if (!window.confirm(`Are you sure you want to PERMANENTLY delete ${userToDelete.first_name} ${userToDelete.last_name} (${userToDelete.email})?\n\nThis action cannot be undone and will remove all user data forever.`)) {
      return;
    }

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.delete(`${apiBase}/admin/users/${userId}/permanent`, { headers });

      // Show success message
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-ui-green-bg border border-ui-green-line text-ui-green-fg px-4 py-3 rounded-lg shadow-ui-pop z-50';
      successMessage.textContent = 'User permanently deleted';
      document.body.appendChild(successMessage);
      setTimeout(() => {
        if (document.body.contains(successMessage)) {
          document.body.removeChild(successMessage);
        }
      }, 3000);

      loadData(); // Reload to get updated user list
    } catch (err) {
      setError('Failed to permanently delete user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/users/${userId}/role`, { role: newRole }, { headers });
      loadData();
    } catch (err) {
      setError('Failed to update user role: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdateOrgAdmin = async (userId, isOrgAdmin) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/admin/users/${userId}/organization-admin`, 
        { is_organization_admin: isOrgAdmin }, 
        { headers }
      );
      loadData(); // Refresh user list
    } catch (err) {
      setError('Failed to update organization admin status: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (user?.role !== 'superadmin') {
    return (
      <div className="text-center py-12">
        <Ban className="w-12 h-12 mx-auto mb-4 text-ui-red-fg" aria-hidden="true" />
        <h2 className="text-xl font-semibold text-ui-ink mb-2">Access Denied</h2>
        <p className="text-ui-muted">This area is restricted to superadmin users only.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ui-primary"></div>
      </div>
    );
  }

  // Sections of this page, shown as tabs. The app sidebar links to the
  // other superadmin pages (settings, billing scheduler).
  const tabs = [
    { id: 'organizations', label: 'Organizations', icon: Building2 },
    { id: 'packages', label: 'Packages', icon: Package },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'invites', label: 'Invites', icon: Mail, count: invites.length },
    { id: 'deleted', label: 'Deleted orgs', icon: Archive },
    { id: 'parsing', label: 'Parsing feedback', icon: AlertTriangle },
    { id: 'rejected', label: 'Rejected emails', icon: Ban },
    { id: 'flightaware', label: 'FlightAware', icon: Plane },
    { id: 'aiusage', label: 'AI usage', icon: Activity },
    { id: 'domains', label: 'Domains', icon: Globe },
    { id: 'settings', label: 'Export settings', icon: Settings2 },
  ];

  const selectTabByKey = (e, index) => {
    const next = e.key === 'ArrowRight' ? index + 1 : e.key === 'ArrowLeft' ? index - 1 : null;
    if (next === null) return;
    e.preventDefault();
    const tab = tabs[(next + tabs.length) % tabs.length];
    setActiveTab(tab.id);
    document.getElementById(`superadmin-tab-${tab.id}`)?.focus();
  };

  return (
    <div className="max-w-[1120px] flex flex-col gap-5">
      <header>
        <h1 className="m-0 text-2xl font-bold tracking-tight text-ui-ink">Superadmin</h1>
        <p className="mt-1 text-sm text-ui-muted">Manage organizations, users, and system settings.</p>
      </header>

      <div role="tablist" aria-label="Superadmin sections" className="flex gap-1 overflow-x-auto border-b border-ui-divider -mx-1 px-1">
        {tabs.map((tab, index) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              id={`superadmin-tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls="superadmin-tabpanel"
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(e) => selectTabByKey(e, index)}
              className={`flex-none inline-flex items-center gap-2 h-11 px-3 -mb-px border-b-2 text-sm whitespace-nowrap ${
                isActive ? 'border-ui-primary text-ui-ink font-semibold' : 'border-transparent text-ui-ink-3 hover:text-ui-ink'
              }`}
            >
              <Icon className="w-4 h-4 flex-none" aria-hidden="true" />
              {tab.label}
              {tab.count > 0 && (
                <span className="text-xs font-semibold px-[7px] py-px rounded-full bg-ui-amber-bg text-ui-amber-fg">{tab.count}</span>
              )}
            </button>
          );
        })}
      </div>

      <div id="superadmin-tabpanel" role="tabpanel" aria-labelledby={`superadmin-tab-${activeTab}`} className="min-w-0 flex flex-col gap-5">
          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 bg-ui-red-bg border border-ui-red-line text-ui-red-fg px-4 py-3 rounded-lg text-sm" role="alert">
              <span className="flex-1">{error}</span>
              <button
                type="button"
                onClick={() => setError('')}
                className="text-ui-red-fg hover:opacity-80"
                aria-label="Dismiss error"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
          )}

          {/* Tab Content */}
          {activeTab === 'organizations' && (
            <OrganizationsTab
              organizations={organizations}
              packages={packages}
              onCreateOrganization={() => setShowCreateOrgModal(true)}
              onDeleteOrganization={handleDeleteOrganization}
              onManageAdmins={handleManageOrgAdmins}
              onComponentSettings={handleComponentSettings}
              onSSODomains={handleSSODomains}
              onAssignPackage={() => setShowAssignPackageModal(true)}
            />
          )}

          {activeTab === 'packages' && (
            <PackagesTab
              packages={packages}
              onCreatePackage={() => {
                setSelectedPackage(null);
                setShowPackageFormModal(true);
              }}
              onEditPackage={(pkg) => {
                setSelectedPackage(pkg);
                setShowPackageFormModal(true);
              }}
              onDeletePackage={handleDeletePackage}
              onAssignPackage={() => setShowAssignPackageModal(true)}
            />
          )}

          {activeTab === 'users' && (
            <UsersTab
              users={users}
              organizations={organizations}
              onInviteUser={() => setShowInviteUserModal(true)}
              onRemoveUser={handleRemoveUser}
              onUpdateUserRole={handleUpdateUserRole}
              onUpdateOrgAdmin={handleUpdateOrgAdmin}
              showInactiveUsers={showInactiveUsers}
              onToggleInactiveUsers={() => setShowInactiveUsers(!showInactiveUsers)}
              onReactivateUser={handleReactivateUser}
              onPermanentlyDeleteUser={handlePermanentlyDeleteUser}
            />
          )}

          {activeTab === 'invites' && (
            <InvitesTab
              invites={invites}
              onApproveInvite={handleApproveInvite}
              onRejectInvite={handleRejectInvite}
            />
          )}

          {activeTab === 'parsing' && (
            <ParsingFeedbackAdmin />
          )}

          {activeTab === 'flightaware' && (
            <FlightAwareLogs />
          )}

          {activeTab === 'aiusage' && (
            <AIUsageLogs />
          )}

          {activeTab === 'rejected' && (
            <RejectedEmailLogs />
          )}

          {activeTab === 'domains' && (
            <DomainsTab
              domains={emailDomains}
              onRefresh={loadData}
              onError={setError}
              setDomains={setEmailDomains}
            />
          )}

          {activeTab === 'settings' && (
            <SystemSettingsTab
              settings={systemSettings}
              onUpdate={setSystemSettings}
              onError={setError}
            />
          )}

          {activeTab === 'deleted' && (
            <DeletedOrganizationsTab
              deletedOrganizations={deletedOrganizations}
              onReinstateOrganization={handleReinstateOrganization}
              onPermanentlyDelete={handlePermanentlyDelete}
              onRefresh={loadData}
            />
          )}
      </div>

      {/* Modals */}
      {showCreateOrgModal && (
        <CreateOrganizationModal
          users={users}
          onClose={() => setShowCreateOrgModal(false)}
          onCreate={handleCreateOrganization}
        />
      )}

      {showInviteUserModal && (
        <InviteUserModal
          organizations={organizations}
          onClose={() => setShowInviteUserModal(false)}
          onInvite={handleInviteUser}
        />
      )}

      {showInviteLinkModal && inviteResult && (
        <InviteLinkModal
          inviteLink={inviteResult.inviteLink}
          inviteToken={inviteResult.inviteToken}
          email={inviteResult.email}
          organizationName={inviteResult.organizationName}
          role={inviteResult.role}
          emailSentAutomatically={inviteResult.emailSent}
          onClose={() => {
            setShowInviteLinkModal(false);
            setInviteResult(null);
          }}
          onSendEmail={null} // We'll implement email sending later if needed
        />
      )}

      {showUserDeleteModal && userToDelete && (
        <UserDeleteModal
          user={userToDelete}
          onConfirm={confirmUserDelete}
          onCancel={cancelUserDelete}
        />
      )}

      {showOrgDeleteModal && orgToDelete && (
        <OrganizationDeleteModal
          organization={orgToDelete}
          onConfirm={confirmOrganizationDelete}
          onCancel={cancelOrganizationDelete}
        />
      )}

      {showManageAdminsModal && selectedOrganization && (
        <ManageOrgAdminsModal
          organization={selectedOrganization}
          onClose={() => {
            setShowManageAdminsModal(false);
            setSelectedOrganization(null);
          }}
          onUpdate={handleUpdateOrgAdmins}
        />
      )}

      {showComponentSettingsModal && selectedOrgForSettings && (
        <OrganizationComponentSettingsModal
          organization={selectedOrgForSettings}
          onClose={() => {
            setShowComponentSettingsModal(false);
            setSelectedOrgForSettings(null);
          }}
          onUpdate={() => {
            // Optionally refresh organization data if needed
            loadData();
          }}
        />
      )}

      {showSSODomainModal && selectedOrgForSSO && (
        <SSODomainModal
          organization={selectedOrgForSSO}
          currentUser={user}
          onClose={() => {
            setShowSSODomainModal(false);
            setSelectedOrgForSSO(null);
            loadData(); // Reload data to refresh any changes
          }}
        />
      )}

      {/* Package Modals */}
      <PackageFormModal
        isOpen={showPackageFormModal}
        onClose={() => {
          setShowPackageFormModal(false);
          setSelectedPackage(null);
        }}
        onSave={handleSavePackage}
        existingPackage={selectedPackage}
      />

      <AssignPackageModal
        isOpen={showAssignPackageModal}
        onClose={() => setShowAssignPackageModal(false)}
        onSuccess={() => {
          loadData();
          setShowAssignPackageModal(false);
        }}
        organizations={organizations}
        packages={packages}
      />
    </div>
  );
};

// Shared presentation helpers (theme tokens from src/index.css)
const BTN = 'inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-ui-control bg-ui-surface text-[13px] text-ui-ink-2 hover:bg-ui-hover whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_P = 'inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-ui-primary hover:bg-ui-primary-hover text-ui-on-primary text-sm font-semibold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
const IBTN = 'inline-flex items-center justify-center w-9 h-9 rounded-lg text-ui-muted hover:bg-ui-hover hover:text-ui-ink transition-colors';
const IBTN_DANGER = 'inline-flex items-center justify-center w-9 h-9 rounded-lg text-ui-muted hover:bg-ui-red-bg hover:text-ui-red-fg transition-colors';
const IBTN_SUCCESS = 'inline-flex items-center justify-center w-9 h-9 rounded-lg text-ui-muted hover:bg-ui-green-bg hover:text-ui-green-fg transition-colors';
const IBTN_WARN = 'inline-flex items-center justify-center w-9 h-9 rounded-lg text-ui-muted hover:bg-ui-orange-bg hover:text-ui-orange-fg transition-colors';
const FIELD = 'h-10 w-full rounded-lg border border-ui-control bg-ui-surface text-ui-ink text-sm px-3 placeholder:text-ui-faint focus:outline-none focus:ring-2 focus:ring-ui-primary/40';
const FIELD_SM = 'h-8 rounded-lg border border-ui-control bg-ui-surface text-ui-ink text-[13px] px-2 focus:outline-none focus:ring-2 focus:ring-ui-primary/40 disabled:opacity-60';
const CARD = 'bg-ui-surface border border-ui-line rounded-2xl shadow-ui-card overflow-hidden';
const TABLE = 'w-full border-collapse text-sm';
const TH = 'text-left text-xs font-semibold text-ui-muted px-4 py-2.5 bg-ui-sunk border-b border-ui-divider whitespace-nowrap';
const TD = 'px-4 py-3 border-b border-ui-divider text-ui-ink-2 align-middle';
const ROW = 'hover:bg-ui-sunk';
const SR_ONLY = 'sr-only';

const PILL_TONES = {
  blue: 'bg-ui-blue-bg text-ui-blue-fg',
  violet: 'bg-ui-violet-bg text-ui-violet-fg',
  teal: 'bg-ui-teal-bg text-ui-teal-fg',
  green: 'bg-ui-green-bg text-ui-green-fg',
  amber: 'bg-ui-amber-bg text-ui-amber-fg',
  red: 'bg-ui-red-bg text-ui-red-fg',
  orange: 'bg-ui-orange-bg text-ui-orange-fg',
  indigo: 'bg-ui-indigo-bg text-ui-indigo-fg',
  pink: 'bg-ui-pink-bg text-ui-pink-fg',
  neutral: 'bg-ui-hover text-ui-ink-3'
};

const Pill = ({ tone = 'neutral', dot = false, className = '', children }) => (
  <span className={`inline-flex items-center gap-1.5 h-6 px-[9px] rounded-full text-xs font-semibold whitespace-nowrap ${PILL_TONES[tone] || PILL_TONES.neutral} ${className}`}>
    {dot && <span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden="true" />}
    {children}
  </span>
);

const AVATAR_TONES = ['blue', 'teal', 'amber', 'violet', 'red', 'green', 'indigo', 'pink'];

const getInitials = (name = '') => {
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

const getAvatarTone = (key = '') => {
  let hash = 0;
  const str = String(key);
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return AVATAR_TONES[hash % AVATAR_TONES.length];
};

const PanelHeader = ({ title, description, children }) => (
  <header className="flex flex-wrap items-end justify-between gap-4">
    <div>
      <h2 className="m-0 text-xl font-bold tracking-[-0.01em] text-ui-ink">{title}</h2>
      {description && <p className="mt-1 text-sm text-ui-muted">{description}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-3">{children}</div>}
  </header>
);

const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="text-center py-12 px-4">
    <Icon className="w-10 h-10 mx-auto mb-4 text-ui-faint" aria-hidden="true" />
    <h3 className="text-lg font-semibold text-ui-ink mb-2">{title}</h3>
    <p className="text-ui-muted text-sm">{description}</p>
  </div>
);

const Toggle = ({ checked, onChange, label, title, onTone = 'bg-ui-primary' }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!checked}
    aria-label={label}
    title={title}
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 flex-none items-center rounded-full transition-colors ${checked ? onTone : 'bg-ui-control'}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-ui-on-primary shadow-ui-card transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`}
    />
  </button>
);

// Organizations Tab Component
const OrganizationsTab = ({ organizations, packages, onCreateOrganization, onDeleteOrganization, onManageAdmins, onComponentSettings, onSSODomains, onAssignPackage }) => (
  <div className="flex flex-col gap-5">
    <PanelHeader title="Organizations" description="Manage organizations and their settings">
      <button type="button" onClick={onCreateOrganization} className={BTN_P}>
        <Plus className="w-4 h-4" aria-hidden="true" />
        New organization
      </button>
    </PanelHeader>

    <section className={CARD} aria-label="Organizations list">
      <div className="overflow-x-auto">
        <table className={TABLE}>
          <thead>
            <tr>
              <th scope="col" className={TH}>Organization</th>
              <th scope="col" className={`${TH} hidden md:table-cell`}>Domain</th>
              <th scope="col" className={TH}>Users</th>
              <th scope="col" className={`${TH} hidden sm:table-cell`}>Trips</th>
              <th scope="col" className={`${TH} hidden lg:table-cell`}>Status</th>
              <th scope="col" className={TH}><span className={SR_ONLY}>Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {organizations.map((org) => {
              const tone = getAvatarTone(org.id || org.name);
              return (
                <tr key={org.id} className={ROW}>
                  <td className={TD}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 flex-none rounded-lg flex items-center justify-center text-xs font-bold ${PILL_TONES[tone]}`} aria-hidden="true">
                        {getInitials(org.name)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-ui-ink">{org.name}</div>
                        <div className="text-xs text-ui-muted truncate max-w-[150px] sm:max-w-[320px]">{org.description || 'No description'}</div>
                      </div>
                    </div>
                  </td>
                  <td className={`${TD} whitespace-nowrap hidden md:table-cell`}>
                    {org.domain ? org.domain : <span className="text-ui-muted">No domain</span>}
                  </td>
                  <td className={`${TD} whitespace-nowrap`}>
                    {org.user_count}
                  </td>
                  <td className={`${TD} whitespace-nowrap hidden sm:table-cell`}>
                    {org.active_trips}
                  </td>
                  <td className={`${TD} whitespace-nowrap hidden lg:table-cell`}>
                    <Pill tone={org.auto_approval ? 'green' : 'amber'} dot>
                      {org.auto_approval ? 'Auto' : 'Manual'}
                    </Pill>
                  </td>
                  <td className={`${TD} whitespace-nowrap text-right`}>
                    <div className="inline-flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => onComponentSettings(org)}
                        className={IBTN}
                        title="Component Settings"
                        aria-label={`Component settings for ${org.name}`}
                      >
                        <Settings className="w-4 h-4" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onSSODomains(org)}
                        className={IBTN}
                        title="Google SSO Domains"
                        aria-label={`Google SSO domains for ${org.name}`}
                      >
                        <ShieldCheck className="w-4 h-4" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onManageAdmins(org)}
                        className={IBTN}
                        title="Manage Admins"
                        aria-label={`Manage admins for ${org.name}`}
                      >
                        <UserCog className="w-4 h-4" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDeleteOrganization(org.id)}
                        className={IBTN_DANGER}
                        title="Delete Organization"
                        aria-label={`Delete ${org.name}`}
                      >
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {organizations.length === 0 && (
              <tr>
                <td colSpan={6} className={`${TD} text-center text-ui-muted py-8`}>
                  No organizations yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-ui-sunk">
        <span className="text-[13px] text-ui-muted">
          {organizations.length} organization{organizations.length !== 1 ? 's' : ''}
        </span>
      </div>
    </section>
  </div>
);

// Users Tab Component
const UsersTab = ({ users, organizations, onInviteUser, onRemoveUser, onUpdateUserRole, onUpdateOrgAdmin, showInactiveUsers, onToggleInactiveUsers, onReactivateUser, onPermanentlyDeleteUser }) => {
  const inactiveUserCount = users.filter(u => u.is_active === false).length;
  const activeUserCount = users.filter(u => u.is_active !== false).length;

  return (
  <div className="flex flex-col gap-5">
    <PanelHeader
      title="Users"
      description={
        <>
          Manage users across all organizations
          {showInactiveUsers && inactiveUserCount > 0 && (
            <span className="ml-2">
              ({activeUserCount} active, {inactiveUserCount} inactive)
            </span>
          )}
        </>
      }
    >
      <label className={`flex items-center gap-2 text-[13px] cursor-pointer h-9 px-3 rounded-lg transition-colors ${
        showInactiveUsers ? 'bg-ui-orange-bg text-ui-orange-fg' : 'text-ui-ink-3 hover:bg-ui-hover'
      }`}>
        <input
          type="checkbox"
          checked={showInactiveUsers}
          onChange={onToggleInactiveUsers}
          className="h-4 w-4 rounded border-ui-control accent-ui-orange-solid"
        />
        Show inactive users
      </label>
      <button type="button" onClick={onInviteUser} className={BTN_P}>
        <Plus className="w-4 h-4" aria-hidden="true" />
        Invite User
      </button>
    </PanelHeader>

    {/* Inactive users info banner */}
    {showInactiveUsers && inactiveUserCount > 0 && (
      <div className="bg-ui-orange-bg border border-ui-orange-line rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-ui-orange-fg mt-0.5 flex-none" aria-hidden="true" />
          <div>
            <h3 className="text-sm font-semibold text-ui-orange-fg">
              {inactiveUserCount} Inactive User{inactiveUserCount !== 1 ? 's' : ''} Found
            </h3>
            <div className="mt-1 text-sm text-ui-ink-2">
              <p>Inactive users are shown with a grayed-out row and cannot log in.</p>
              <p className="mt-1">
                <strong>To deactivate a user:</strong> Click the trash icon next to an active user.
              </p>
              <p className="mt-1">
                <strong>To reactivate:</strong> Click the refresh icon. <strong>To permanently delete:</strong> Click the red trash icon (removes all data).
              </p>
            </div>
          </div>
        </div>
      </div>
    )}

    <section className={CARD} aria-label="Users list">
      <div className="overflow-x-auto">
        <table className={TABLE}>
          <thead>
            <tr>
              <th scope="col" className={TH}>User</th>
              <th scope="col" className={`${TH} hidden sm:table-cell`}>Organization</th>
              <th scope="col" className={TH}>Role</th>
              <th scope="col" className={`${TH} hidden sm:table-cell`}>Status</th>
              <th scope="col" className={`${TH} text-center w-16 sm:w-24`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className={`${ROW} ${user.is_active === false ? 'bg-ui-sunk opacity-60' : ''}`}>
                <td className={`${TD} whitespace-nowrap`}>
                  <div>
                    <div className="font-semibold text-ui-ink">
                      {user.first_name} {user.last_name}
                      {user.is_active === false && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-ui-hover text-ui-ink-3 sm:hidden">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-ui-muted truncate max-w-[120px] sm:max-w-none">{user.email}</div>
                    <div className="text-xs text-ui-faint sm:hidden">{organizations.find(org => org.id === user.organization_id)?.name}</div>
                  </div>
                </td>
                <td className={`${TD} whitespace-nowrap hidden sm:table-cell`}>
                  {organizations.find(org => org.id === user.organization_id)?.name || user.organization_id}
                </td>
                <td className={`${TD} whitespace-nowrap`}>
                  <div className="space-y-2">
                    <select
                      value={user.role}
                      onChange={(e) => onUpdateUserRole(user.id, e.target.value)}
                      className={`${FIELD_SM} w-full max-w-[100px] sm:max-w-none`}
                      disabled={user.is_active === false}
                      aria-label={`Role for ${user.first_name} ${user.last_name}`}
                    >
                      <option value="ea">EA</option>
                      <option value="executive">Executive</option>
                      <option value="other">Other</option>
                    </select>

                    {/* Organization Admin Checkbox */}
                    <label className="flex items-center gap-1 text-xs text-ui-ink-3">
                      <input
                        type="checkbox"
                        checked={user.is_organization_admin || false}
                        onChange={(e) => onUpdateOrgAdmin(user.id, e.target.checked)}
                        className="h-3 w-3 rounded border-ui-control accent-ui-primary"
                        disabled={user.is_active === false}
                      />
                      <span className="hidden sm:inline">Organization </span>Admin
                    </label>
                  </div>
                </td>
                <td className={`${TD} whitespace-nowrap hidden sm:table-cell`}>
                  {user.is_active === false ? (
                    <Pill tone="neutral" dot>Inactive</Pill>
                  ) : (
                    <Pill tone="green" dot>Active</Pill>
                  )}
                </td>
                <td className={`${TD} whitespace-nowrap`}>
                  <div className="flex justify-center gap-0.5">
                    {user.is_active === false ? (
                      <>
                        <button
                          type="button"
                          onClick={() => onReactivateUser && onReactivateUser(user.id)}
                          className={IBTN_SUCCESS}
                          title="Reactivate User"
                          aria-label={`Reactivate ${user.first_name} ${user.last_name}`}
                        >
                          <RefreshCw className="w-4 h-4" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onPermanentlyDeleteUser && onPermanentlyDeleteUser(user.id)}
                          className={`${IBTN_DANGER} text-ui-red-fg`}
                          title="Permanently Delete User"
                          aria-label={`Permanently delete ${user.first_name} ${user.last_name}`}
                        >
                          <Trash2 className="w-4 h-4" aria-hidden="true" />
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onRemoveUser(user.id)}
                        className={IBTN_WARN}
                        title="Deactivate User"
                        aria-label={`Deactivate ${user.first_name} ${user.last_name}`}
                      >
                        <Ban className="w-4 h-4" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  </div>
);
};

// Invites Tab Component
const InvitesTab = ({ invites, onApproveInvite, onRejectInvite }) => (
  <div className="flex flex-col gap-5">
    <PanelHeader title="Pending Invites" description="Review and approve user invitations" />

    <section className={CARD} aria-label="Pending invites">
      {invites.length === 0 ? (
        <EmptyState icon={Mail} title="No pending invites" description="All invitations have been processed" />
      ) : (
        <div className="overflow-x-auto">
          <table className={TABLE}>
            <thead>
              <tr>
                <th scope="col" className={TH}>Email</th>
                <th scope="col" className={`${TH} hidden sm:table-cell`}>Organization</th>
                <th scope="col" className={TH}>Role</th>
                <th scope="col" className={`${TH} hidden md:table-cell`}>Invited By</th>
                <th scope="col" className={`${TH} text-center w-20 sm:w-24`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((invite) => (
                <tr key={invite.id} className={ROW}>
                  <td className={`${TD} whitespace-nowrap`}>
                    <div className="font-semibold text-ui-ink truncate max-w-[120px] sm:max-w-none">{invite.email}</div>
                    <div className="text-xs text-ui-faint sm:hidden">{invite.organization_name}</div>
                  </td>
                  <td className={`${TD} whitespace-nowrap hidden sm:table-cell`}>
                    {invite.organization_name}
                  </td>
                  <td className={`${TD} whitespace-nowrap`}>
                    <Pill tone="blue">{invite.role}</Pill>
                  </td>
                  <td className={`${TD} whitespace-nowrap text-ui-muted hidden md:table-cell`}>
                    {invite.invited_by_name}
                  </td>
                  <td className={`${TD} whitespace-nowrap`}>
                    <div className="flex justify-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => onApproveInvite(invite.id)}
                        className={IBTN_SUCCESS}
                        title="Approve Invite"
                        aria-label={`Approve invite for ${invite.email}`}
                      >
                        <Check className="w-4 h-4" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onRejectInvite(invite.id)}
                        className={IBTN_DANGER}
                        title="Reject Invite"
                        aria-label={`Reject invite for ${invite.email}`}
                      >
                        <X className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  </div>
);

// Deleted Organizations Tab Component
const DeletedOrganizationsTab = ({ deletedOrganizations, onReinstateOrganization, onPermanentlyDelete, onRefresh }) => {
  const formatTimeRemaining = (reinstateBeforeDate) => {
    const now = new Date();
    const deadline = new Date(reinstateBeforeDate);
    const diffTime = deadline - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return "Expired";
    if (diffDays === 1) return "1 day";
    return `${diffDays} days`;
  };

  const formatDeletedDate = (deletedAtDate) => {
    return new Date(deletedAtDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex flex-col gap-5">
      <PanelHeader title="Deleted Organizations" description="Organizations pending deletion (30-day retention period)">
        <button type="button" onClick={onRefresh} className={BTN}>
          <RefreshCw className="w-4 h-4" aria-hidden="true" />
          Refresh
        </button>
      </PanelHeader>

      <section className={CARD} aria-label="Deleted organizations">
        {deletedOrganizations.length === 0 ? (
          <EmptyState icon={Archive} title="No deleted organizations" description="Organizations deleted in the last 30 days will appear here" />
        ) : (
          <div className="overflow-x-auto">
            <table className={TABLE}>
              <thead>
                <tr>
                  <th scope="col" className={TH}>Organization</th>
                  <th scope="col" className={`${TH} hidden md:table-cell`}>Deleted At</th>
                  <th scope="col" className={`${TH} hidden lg:table-cell`}>Deleted By</th>
                  <th scope="col" className={TH}>Time Left</th>
                  <th scope="col" className={`${TH} text-center w-20 sm:w-24`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {deletedOrganizations.map((org) => {
                  const timeRemaining = formatTimeRemaining(org.reinstate_before);
                  const isExpired = timeRemaining === "Expired";

                  return (
                    <tr key={org.id} className={`${ROW} ${isExpired ? 'bg-ui-red-bg' : ''}`}>
                      <td className={`${TD} whitespace-nowrap`}>
                        <div>
                          <div className="font-semibold text-ui-ink">{org.name}</div>
                          <div className="text-xs text-ui-muted truncate max-w-[120px] sm:max-w-none">{org.description || 'No description'}</div>
                          {org.domain && (
                            <div className="text-xs text-ui-faint hidden sm:block">Domain: {org.domain}</div>
                          )}
                        </div>
                      </td>
                      <td className={`${TD} whitespace-nowrap hidden md:table-cell`}>
                        {formatDeletedDate(org.deleted_at)}
                      </td>
                      <td className={`${TD} whitespace-nowrap hidden lg:table-cell`}>
                        {org.deleted_by_name || 'Unknown'}
                      </td>
                      <td className={`${TD} whitespace-nowrap`}>
                        <Pill
                          dot
                          tone={
                            isExpired
                              ? 'red'
                              : timeRemaining.includes('1 day') || timeRemaining.includes('2 day') || timeRemaining.includes('3 day')
                                ? 'amber'
                                : 'green'
                          }
                        >
                          {isExpired ? 'Expired' : `${timeRemaining}`}
                        </Pill>
                      </td>
                      <td className={`${TD} whitespace-nowrap`}>
                        <div className="flex justify-center gap-0.5">
                          {!isExpired ? (
                            <button
                              type="button"
                              onClick={() => onReinstateOrganization(org.id)}
                              className={IBTN_SUCCESS}
                              title="Reinstate Organization"
                              aria-label={`Reinstate ${org.name}`}
                            >
                              <RefreshCw className="w-4 h-4" aria-hidden="true" />
                            </button>
                          ) : (
                            <div className="inline-flex items-center justify-center w-9 h-9 text-ui-faint" title="Cannot reinstate (expired)">
                              <Ban className="w-4 h-4" aria-hidden="true" />
                              <span className={SR_ONLY}>Cannot reinstate (expired)</span>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => onPermanentlyDelete(org.id)}
                            className={IBTN_DANGER}
                            title="Delete Forever"
                            aria-label={`Delete ${org.name} forever`}
                          >
                            <Trash2 className="w-4 h-4" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {deletedOrganizations.length > 0 && (
        <div className="bg-ui-amber-bg border border-ui-amber-line rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-ui-amber-fg mt-0.5 flex-none" aria-hidden="true" />
            <div>
              <h3 className="text-sm font-semibold text-ui-amber-fg">Retention Policy</h3>
              <div className="mt-1 text-sm text-ui-ink-2">
                <p>• Organizations are retained for 30 days after deletion</p>
                <p>• All users and data are preserved during this period</p>
                <p>• Organizations can be reinstated at any time before the deadline</p>
                <p>• After 30 days, data will be permanently deleted and cannot be recovered</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Packages Tab Component
const PackagesTab = ({ packages, onCreatePackage, onEditPackage, onDeletePackage, onAssignPackage }) => (
  <div className="flex flex-col gap-5">
    <PanelHeader title="Packages" description="Manage subscription packages and assign them to organizations">
      <button type="button" onClick={onAssignPackage} className={`${BTN} h-10`}>
        Assign Package
      </button>
      <button type="button" onClick={onCreatePackage} className={BTN_P}>
        <Plus className="w-4 h-4" aria-hidden="true" />
        Create Package
      </button>
    </PanelHeader>

    <section className={CARD} aria-label="Packages list">
      {packages.length === 0 ? (
        <EmptyState icon={Package} title="No packages yet" description="Create your first package to get started" />
      ) : (
        <div className="overflow-x-auto">
          <table className={TABLE}>
            <thead>
              <tr>
                <th scope="col" className={TH}>Package</th>
                <th scope="col" className={TH}>Price</th>
                <th scope="col" className={`${TH} hidden md:table-cell`}>Features</th>
                <th scope="col" className={`${TH} hidden lg:table-cell`}>Assignments</th>
                <th scope="col" className={`${TH} hidden sm:table-cell`}>Status</th>
                <th scope="col" className={`${TH} text-center w-20 sm:w-24`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {packages.map((pkg) => (
                <tr key={pkg.id} className={ROW}>
                  <td className={`${TD} whitespace-nowrap`}>
                    <div>
                      <div className="font-semibold text-ui-ink">{pkg.name}</div>
                      <div className="text-xs text-ui-muted truncate max-w-[100px] sm:max-w-[150px]">{pkg.description || 'No description'}</div>
                      {pkg.is_trial && (
                        <Pill tone="orange" className="mt-1">Trial</Pill>
                      )}
                    </div>
                  </td>
                  <td className={`${TD} whitespace-nowrap`}>
                    {pkg.base_price > 0 ? `${pkg.base_price} ${pkg.currency}` : 'Free'}
                  </td>
                  <td className={`${TD} whitespace-nowrap hidden md:table-cell`}>
                    <div className="flex flex-wrap gap-1">
                      {pkg.teams_enabled && <Pill tone="blue">Teams</Pill>}
                      {pkg.flight_alerts_enabled && <Pill tone="violet">Alerts</Pill>}
                      {pkg.sso_enabled && <Pill tone="green">SSO</Pill>}
                      {pkg.dedicated_emails_executives && <Pill tone="indigo">Exec Email</Pill>}
                    </div>
                  </td>
                  <td className={`${TD} whitespace-nowrap hidden lg:table-cell`}>
                    <span className="text-ui-green-fg font-semibold">{pkg.active_assignments || 0}</span>
                    <span className="text-ui-faint"> / {pkg.total_assignments || 0}</span>
                  </td>
                  <td className={`${TD} whitespace-nowrap hidden sm:table-cell`}>
                    <Pill
                      dot
                      tone={pkg.status === 'active' ? 'green' : pkg.status === 'inactive' ? 'amber' : 'neutral'}
                    >
                      {pkg.status}
                    </Pill>
                  </td>
                  <td className={`${TD} whitespace-nowrap`}>
                    <div className="flex justify-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => onEditPackage(pkg)}
                        className={IBTN}
                        title="Edit Package"
                        aria-label={`Edit ${pkg.name}`}
                      >
                        <Pencil className="w-4 h-4" aria-hidden="true" />
                      </button>
                      {pkg.status !== 'archived' && (
                        <button
                          type="button"
                          onClick={() => onDeletePackage(pkg.id)}
                          className={IBTN_DANGER}
                          title="Archive Package"
                          aria-label={`Archive ${pkg.name}`}
                        >
                          <Archive className="w-4 h-4" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  </div>
);

// Domains Tab Component
const DomainsTab = ({ domains, onRefresh, onError, setDomains }) => {
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [newDomain, setNewDomain] = React.useState({ domain: '', label: '', description: '' });
  const [saving, setSaving] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [editForm, setEditForm] = React.useState({});

  // Load domains on mount if empty
  React.useEffect(() => {
    if (domains.length === 0) {
      loadDomains();
    }
  }, []);

  const loadDomains = async () => {
    try {
      const data = await listEmailDomains(true);
      setDomains(data);
    } catch (err) {
      console.error('Error loading domains:', err);
      onError('Failed to load domains: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.domain || !newDomain.label) return;
    setSaving(true);
    try {
      await addEmailDomain(newDomain);
      setNewDomain({ domain: '', label: '', description: '' });
      setShowAddForm(false);
      await loadDomains();
    } catch (err) {
      onError('Failed to add domain: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (domain) => {
    try {
      await updateEmailDomain(domain.id, { is_active: !domain.is_active });
      await loadDomains();
    } catch (err) {
      onError('Failed to update domain: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSetPrimary = async (domain) => {
    try {
      await setPrimaryDomain(domain.id);
      await loadDomains();
    } catch (err) {
      onError('Failed to set primary domain: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (domain) => {
    if (!window.confirm(`Are you sure you want to delete the domain "${domain.domain}"?`)) return;
    try {
      await deleteEmailDomain(domain.id);
      await loadDomains();
    } catch (err) {
      onError('Failed to delete domain: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleStartEdit = (domain) => {
    setEditingId(domain.id);
    setEditForm({ label: domain.label, description: domain.description || '' });
  };

  const handleSaveEdit = async (domain) => {
    try {
      await updateEmailDomain(domain.id, editForm);
      setEditingId(null);
      await loadDomains();
    } catch (err) {
      onError('Failed to update domain: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <PanelHeader
        title="Email Domains"
        description="Manage email domains for inbound email processing. Emails sent to any active domain will be processed the same way."
      >
        <button
          type="button"
          onClick={() => setShowAddForm(!showAddForm)}
          className={BTN_P}
          aria-expanded={showAddForm}
        >
          <Plus className="w-4 h-4" aria-hidden="true" />
          Add Domain
        </button>
      </PanelHeader>

      {/* Add Domain Form */}
      {showAddForm && (
        <section className={`${CARD} p-6`} aria-label="Add new domain">
          <h3 className="text-base font-semibold text-ui-ink mb-4">Add New Domain</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="sa-new-domain" className="block text-[13px] font-medium text-ui-ink-3 mb-1">Domain</label>
              <input
                id="sa-new-domain"
                type="text"
                value={newDomain.domain}
                onChange={(e) => setNewDomain({ ...newDomain, domain: e.target.value })}
                placeholder="e.g., new.veyage.io"
                className={FIELD}
              />
            </div>
            <div>
              <label htmlFor="sa-new-domain-label" className="block text-[13px] font-medium text-ui-ink-3 mb-1">Label</label>
              <input
                id="sa-new-domain-label"
                type="text"
                value={newDomain.label}
                onChange={(e) => setNewDomain({ ...newDomain, label: e.target.value })}
                placeholder="e.g., Veyage New"
                className={FIELD}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="sa-new-domain-description" className="block text-[13px] font-medium text-ui-ink-3 mb-1">Description (optional)</label>
              <input
                id="sa-new-domain-description"
                type="text"
                value={newDomain.description}
                onChange={(e) => setNewDomain({ ...newDomain, description: e.target.value })}
                placeholder="Brief description of this domain's purpose"
                className={FIELD}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="button"
              onClick={() => { setShowAddForm(false); setNewDomain({ domain: '', label: '', description: '' }); }}
              className={`${BTN} h-10`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddDomain}
              disabled={saving || !newDomain.domain || !newDomain.label}
              className={BTN_P}
            >
              {saving ? 'Adding...' : 'Add Domain'}
            </button>
          </div>
        </section>
      )}

      {/* Info Box */}
      <div className="bg-ui-blue-bg border border-ui-blue-line rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-ui-blue-fg mt-0.5 flex-none" aria-hidden="true" />
          <p className="text-sm text-ui-ink-2">
            All active domains are equivalent for email processing. Emails sent to <strong className="text-ui-ink">plans@veyage.io</strong> and{' '}
            <strong className="text-ui-ink">plans@in.veyage.io</strong> are treated identically. The <strong className="text-ui-ink">primary</strong> domain is used when
            creating new email addresses (e.g., for EA personal inboxes).
          </p>
        </div>
      </div>

      {/* Domains Table */}
      <section className={CARD} aria-label="Email domains">
        <div className="overflow-x-auto">
          <table className={TABLE}>
            <thead>
              <tr>
                <th scope="col" className={TH}>Domain</th>
                <th scope="col" className={`${TH} hidden sm:table-cell`}>Label</th>
                <th scope="col" className={`${TH} hidden md:table-cell`}>Description</th>
                <th scope="col" className={TH}>Status</th>
                <th scope="col" className={`${TH} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((domain) => (
                <tr key={domain.id} className={`${ROW} ${!domain.is_active ? 'bg-ui-sunk opacity-60' : ''}`}>
                  <td className={`${TD} whitespace-nowrap`}>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-ui-ink">{domain.domain}</span>
                      {domain.is_primary && (
                        <Pill tone="blue">Primary</Pill>
                      )}
                    </div>
                  </td>
                  <td className={`${TD} whitespace-nowrap hidden sm:table-cell`}>
                    {editingId === domain.id ? (
                      <input
                        type="text"
                        value={editForm.label}
                        onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                        className={`${FIELD_SM} w-full`}
                        aria-label={`Label for ${domain.domain}`}
                      />
                    ) : (
                      <span>{domain.label}</span>
                    )}
                  </td>
                  <td className={`${TD} hidden md:table-cell`}>
                    {editingId === domain.id ? (
                      <input
                        type="text"
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className={`${FIELD_SM} w-full`}
                        aria-label={`Description for ${domain.domain}`}
                      />
                    ) : (
                      <span className="text-ui-muted">{domain.description || '—'}</span>
                    )}
                  </td>
                  <td className={`${TD} whitespace-nowrap`}>
                    <Toggle
                      checked={domain.is_active}
                      onChange={() => handleToggleActive(domain)}
                      onTone="bg-ui-green-solid"
                      label={`${domain.domain} active`}
                      title={domain.is_active ? 'Active - click to deactivate' : 'Inactive - click to activate'}
                    />
                  </td>
                  <td className={`${TD} whitespace-nowrap text-right`}>
                    <div className="flex items-center justify-end gap-0.5">
                      {editingId === domain.id ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleSaveEdit(domain)}
                            className={IBTN_SUCCESS}
                            title="Save"
                            aria-label={`Save ${domain.domain}`}
                          >
                            <Check className="w-4 h-4" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingId(null)}
                            className={IBTN}
                            title="Cancel"
                            aria-label={`Cancel editing ${domain.domain}`}
                          >
                            <X className="w-4 h-4" aria-hidden="true" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => handleStartEdit(domain)}
                            className={IBTN}
                            title="Edit"
                            aria-label={`Edit ${domain.domain}`}
                          >
                            <Pencil className="w-4 h-4" aria-hidden="true" />
                          </button>
                          {!domain.is_primary && domain.is_active && (
                            <button
                              type="button"
                              onClick={() => handleSetPrimary(domain)}
                              className={IBTN}
                              title="Set as Primary"
                              aria-label={`Set ${domain.domain} as primary`}
                            >
                              <Star className="w-4 h-4" aria-hidden="true" />
                            </button>
                          )}
                          {!domain.is_primary && (
                            <button
                              type="button"
                              onClick={() => handleDelete(domain)}
                              className={IBTN_DANGER}
                              title="Delete"
                              aria-label={`Delete ${domain.domain}`}
                            >
                              <Trash2 className="w-4 h-4" aria-hidden="true" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {domains.length === 0 && (
                <tr>
                  <td colSpan={5} className={`${TD} text-center text-ui-muted py-8`}>
                    No email domains configured. Add a domain to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Last Updated Info */}
      {domains.length > 0 && domains[0].updated_at && (
        <div className="text-sm text-ui-muted">
          Last updated: {new Date(domains[0].updated_at).toLocaleString()}
          {domains[0].updated_by_email && ` by ${domains[0].updated_by_email}`}
        </div>
      )}
    </div>
  );
};

// System Settings Tab Component
// Note: AI model settings have been moved to the dedicated "AI & Parsing Settings" page
// This tab now only manages Parsing Feedback Export settings
const SystemSettingsTab = ({ settings, onUpdate, onError }) => {
  const [saving, setSaving] = React.useState(false);
  const [localSettings, setLocalSettings] = React.useState(settings?.parsing_feedback || {
    s3_export_enabled: true,
    github_commit_enabled: true
  });

  React.useEffect(() => {
    if (settings?.parsing_feedback) {
      setLocalSettings(settings.parsing_feedback);
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const response = await axios.put(
        `${apiBase}/admin/system-settings`,
        {
          parsing_feedback: localSettings
        },
        { headers }
      );

      onUpdate(response.data);
    } catch (err) {
      console.error('Error saving system settings:', err);
      onError('Failed to save settings: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const toggleSetting = (key) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const hasChanges = settings?.parsing_feedback &&
    (localSettings.s3_export_enabled !== settings.parsing_feedback.s3_export_enabled ||
     localSettings.github_commit_enabled !== settings.parsing_feedback.github_commit_enabled);

  return (
    <div className="flex flex-col gap-5">
      <PanelHeader title="Export Settings" description="Configure parsing feedback export options">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className={BTN_P}
        >
          {saving ? (
            <>
              <Loader2 className="animate-spin h-4 w-4" aria-hidden="true" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </PanelHeader>

      {/* Parsing Feedback Export Settings */}
      <section className={CARD} aria-labelledby="sa-export-heading">
        <div className="px-6 py-4 border-b border-ui-divider bg-ui-sunk">
          <h3 id="sa-export-heading" className="text-base font-semibold text-ui-ink">Parsing Feedback Export</h3>
          <p className="text-sm text-ui-muted mt-1">Configure how parsing feedback is exported for Claude Code analysis</p>
        </div>
        <div className="p-6 space-y-6">
          {/* S3 Export Toggle */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Cloud className="w-5 h-5 text-ui-muted" aria-hidden="true" />
                <h4 className="text-sm font-semibold text-ui-ink">S3 Export (Presigned URLs)</h4>
              </div>
              <p className="text-sm text-ui-muted mt-1 ml-7">
                Export feedback with S3 presigned URLs for document access. URLs expire after 24 hours.
              </p>
            </div>
            <Toggle
              checked={localSettings.s3_export_enabled}
              onChange={() => toggleSetting('s3_export_enabled')}
              label="S3 Export (Presigned URLs)"
            />
          </div>

          {/* GitHub Commit Toggle */}
          <div className="flex items-center justify-between gap-4 border-t border-ui-divider pt-6">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <GitCommit className="w-5 h-5 text-ui-muted" aria-hidden="true" />
                <h4 className="text-sm font-semibold text-ui-ink">GitHub Commit (Send to Claude Code)</h4>
              </div>
              <p className="text-sm text-ui-muted mt-1 ml-7">
                Commit feedback documents directly to GitHub repository for Claude Code access. Recommended for Claude Code workflows.
              </p>
            </div>
            <Toggle
              checked={localSettings.github_commit_enabled}
              onChange={() => toggleSetting('github_commit_enabled')}
              label="GitHub Commit (Send to Claude Code)"
            />
          </div>

          {/* Warning if both are disabled */}
          {!localSettings.s3_export_enabled && !localSettings.github_commit_enabled && (
            <div className="mt-4 p-4 bg-ui-amber-bg border border-ui-amber-line rounded-lg">
              <div className="flex items-center gap-2 text-ui-amber-fg text-sm">
                <AlertTriangle className="w-5 h-5 flex-none" aria-hidden="true" />
                <span className="font-semibold">Warning:</span>
                <span>Both export methods are disabled. You won't be able to export parsing feedback.</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* AI Settings Info Box - points to new settings page */}
      <div className="bg-ui-blue-bg border border-ui-blue-line rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Bot className="w-5 h-5 text-ui-blue-fg mt-0.5 flex-none" aria-hidden="true" />
          <div>
            <h3 className="text-sm font-semibold text-ui-blue-fg">AI & Parsing Settings</h3>
            <p className="text-sm text-ui-ink-2 mt-1">
              AI model configuration has moved to the dedicated{' '}
              <a href="/superadmin-settings" className="font-medium underline text-ui-link hover:text-ui-blue-fg">
                AI & Parsing Settings
              </a>{' '}
              page under Parsing & Data menu.
            </p>
          </div>
        </div>
      </div>

      {/* Last Updated Info */}
      {settings?.updated_at && (
        <div className="text-sm text-ui-muted">
          Last updated: {new Date(settings.updated_at).toLocaleString()}
          {settings.updated_by_user_email && ` by ${settings.updated_by_user_email}`}
        </div>
      )}
    </div>
  );
};

export default SuperadminDashboard;
